import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import { RoleRef, UserCompanyDetail } from "../types";
import { queryKeys } from "./queryKeys";

const onGetUserById = async (userId: string): Promise<UserCompanyDetail> => {
  const { data } = await axios.get(`/user/company/user/${userId}`);
  return data;
};

export const useUserById = (userId: string) =>
  useQuery<UserCompanyDetail>({
    queryKey: [queryKeys.USER, userId],
    queryFn: () => onGetUserById(userId),
    enabled: !!userId,
  });

const onGetUsersByCompany = async (): Promise<UserCompanyDetail[]> => {
  const { data } = await axios.get(`/user/company`);
  return data;
};

export const useUsersByCompany = (userId: string) =>
  useQuery({
    queryKey: [queryKeys.USERS_BY_COMPANY, userId],
    queryFn: onGetUsersByCompany,
    select: (data) => data?.filter((u: any) => u.id !== userId),
  });

export interface PaginatedUsersResponse {
  content: UserCompanyDetail[];
  totalElements: number;
  totalPages: number;
  number: number;
}

const onGetPaginatedUsersByCompany = async (
  page: number,
  size: number,
  query: string
): Promise<PaginatedUsersResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  if (query) params.append("query", query);
  const { data } = await axios.get(
    `/user/company/paginated?${params.toString()}`
  );
  return data;
};

export const usePaginatedUsersByCompany = (
  page: number,
  size: number,
  query: string
) =>
  useQuery<PaginatedUsersResponse>({
    queryKey: [queryKeys.USERS_BY_COMPANY_PAGINATED, page, size, query],
    queryFn: () => onGetPaginatedUsersByCompany(page, size, query),
    placeholderData: (previous) => previous,
  });

const onCreateNewUser = async (body: any) => {
  const { data } = await axios.post(`/user/new`, body);
  return data;
};

export const useCreateNewUser = (onSuccess: () => void, onError: () => void) =>
  useMutation({
    mutationFn: onCreateNewUser,
    onSuccess,
    onError,
  });

const onEditUser = async ({ userId, body }: { userId: string; body: any }) => {
  const { data } = await axios.put(`/user/${userId}`, body);
  return data;
};

export const useUpdateUser = (onSuccess: () => void, onError: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onEditUser,
    onSuccess: () => {
      // Two SEPARATE calls, not one compound key: `invalidateQueries({ queryKey })` matches by
      // PREFIX, so `[queryKeys.USER, queryKeys.USER_MATERIALS_BY_COMPANY]` (the previous code
      // here) only ever matched a query key that literally starts with those two constants back
      // to back — never true for anything real. The detail read's actual key is
      // `[queryKeys.USER, <uuid>]` (useUserById) and the roster's is
      // `[queryKeys.USERS_BY_COMPANY_PAGINATED, page, size, query]` (usePaginatedUsersByCompany);
      // neither is a prefix of the other. This was invisible while PUT /user/{id} silently
      // dropped role/parent writes (a stale cache happened to already hold the right values,
      // since nothing had actually changed server-side) and stayed invisible once that was fixed
      // because `staleTime: 0` — TanStack Query's default — masked it down to a one-frame flash
      // rather than a stuck stale value. Matches the two-call pattern `useSetUserActive` and
      // `useApproveUser` already use correctly, just below.
      queryClient.invalidateQueries({ queryKey: [queryKeys.USER] });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USERS_BY_COMPANY_PAGINATED],
      });
      onSuccess();
    },
    onError,
  });
};

// `currentPassword` (Task 2, Profile/Company flow): the backend's `ChangePassword` DTO
// (spectum/src/main/java/com/sm/spectum/payload/request/ChangePassword.java) now carries it
// alongside `password`, required unless the caller is mid RESET_PASSWORD flow (the controller
// decides that, not the FE) — optional here, widening the body additively, since this is the
// only consumer (grepped: only `containers/Profile/ChangePasswordForm.tsx` calls this hook) and
// the reset-password branch legitimately has none to send.
const onChangePassword = async (body: {
  password: string;
  currentPassword?: string;
}) => {
  const { data } = await axios.post(`/auth/change-password`, body);
  return data;
};

// `onError` widened from `() => void` to take the raw error (same widening `useCreateSite`/
// `useUpdateSite` in `useSites.ts` already went through) so the caller can tell a wrong
// `currentPassword` (400, "Current password is incorrect") apart from any other failure and
// surface it as a field error instead of a generic toast.
export const useChangePassword = (
  onSuccess: () => void,
  onError: (error: unknown) => void
) =>
  useMutation({
    mutationFn: onChangePassword,
    onSuccess,
    onError,
  });

const onChangeLanguage = async ({
  userId,
  language,
}: {
  userId: string;
  language: string;
}) => {
  const { data } = await axios.put(`/user/${userId}/${language}`);
  return data;
};

export const useChangeLanguage = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onChangeLanguage,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER],
      });
      onSuccess();
    },
    onError,
  });
};

const onSetUserActive = async ({
  userId,
  active,
}: {
  userId: string;
  active: boolean;
}) => {
  const { data } = await axios.put(`/user/${userId}/active`, { active });
  return data;
};

export const useSetUserActive = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: onSetUserActive,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USERS_BY_COMPANY_PAGINATED],
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.USER] });
      onSuccess();
    },
    onError,
  });
};

// PUT /user/{id}/approve — the one role transition this flow performs (a colleague awaiting
// admin approval). Deliberately its own endpoint/hook rather than reusing useUpdateUser: Task 3
// removed `role` from PUT /user/{id}'s writable fields, since copying it through there let a
// tenant manager mint a platform superadmin. Approval's destination state is hardcoded
// server-side and rejected unless the target is currently ADMIN_APPROVAL, so no body is sent.
const onApproveUser = async (userId: string) => {
  const { data } = await axios.put(`/user/${userId}/approve`);
  return data;
};

export const useApproveUser = (onSuccess: () => void, onError: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: onApproveUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USERS_BY_COMPANY_PAGINATED],
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.USER] });
      onSuccess();
    },
    onError,
  });
};

// PUT /user/{id}/roles — replaces the user's assigned RBAC roles with exactly `roleIds`
// (needs MANAGE_USERS server-side). Returns the resulting role refs. Invalidates the same two
// caches the other user mutations do: the detail read and the paginated roster (whose rows now
// carry `roles` chips).
const onAssignUserRoles = async ({
  id,
  roleIds,
}: {
  id: string;
  roleIds: string[];
}): Promise<RoleRef[]> => {
  const { data } = await axios.put(`/user/${id}/roles`, { roleIds });
  return data;
};

export const useAssignUserRoles = (
  onSuccess?: () => void,
  onError?: (error: any) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: onAssignUserRoles,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.USER] });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USERS_BY_COMPANY_PAGINATED],
      });
      onSuccess?.();
    },
    onError,
  });
};
